/**
 * Layout for unauthenticated routes. Chrome-less by design — no sidebar,
 * no nav links, no profile chip. Just a gradient backdrop that hosts the
 * sign-in card via `<Outlet />`.
 *
 * If the visitor is already authenticated (e.g. landed on `/signin` from
 * a stale link in a different tab where they signed in), redirects them
 * to `/` immediately so the sign-in screen never mounts under an active
 * session.
 *
 * @agentic connections
 */

import { Suspense } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router";
import { BootSplash } from "@/app/routes/BootSplash";
import { useAuthSession } from "@/app/auth/AuthSessionContext";
import { sanitizeReturnTo } from "@/app/auth/returnTo";

export const UnauthenticatedLayout = () => {
  const { profile, wasAuthenticated, isLoading } = useAuthSession();
  const [searchParams] = useSearchParams();

  // Already-authenticated visitor: bounce them out of the sign-in tree.
  // Honour `?returnTo=` if it's safe; otherwise land on `/`.
  if (profile || wasAuthenticated) {
    const returnTo = sanitizeReturnTo(searchParams.get("returnTo")) ?? "/";
    return <Navigate to={returnTo} replace />;
  }

  // First paint: SDK still resolving. Show the splash so we don't flash
  // an empty backdrop before SignInForm mounts.
  if (isLoading) {
    return <BootSplash />;
  }

  return (
    <Suspense fallback={<BootSplash />}>
      <Outlet />
    </Suspense>
  );
};
